import { decimalSeparator } from "../data/maths/numeric";
import ui from "./ui.module.css";

// The maths keypad shared by numeric entry, expression entry and table cells.
// Every key appends to the end of the entry: there is no cursor to place on a
// tablet. A hardware keyboard can still type into the field itself.

const DIGITS = ["7", "8", "9", "4", "5", "6", "1", "2", "3", "0"];

const Key = ({ label, aria, onPress, disabled, wide = false }) => (
  <button
    type="button"
    className={`${ui.number} ${ui.mathKey} ${wide ? ui.mathKeyWide : ""}`}
    aria-label={aria || label}
    onClick={onPress}
    disabled={disabled}
  >
    {label}
  </button>
);

// The keys a numeric answer may need, and only those (contract §3.3): the
// sign, separator, fraction bar and percent appear only when allowed.
export const numericKeysFor = ({ representations, allowNegative }) => {
  const separator = decimalSeparator();
  const keys = [];
  if (allowNegative) keys.push({ insert: "-", label: "−", aria: "Minus" });
  if (representations.includes("decimal") || representations.includes("percentage")) {
    keys.push({ insert: separator, label: separator, aria: "Decimal point" });
  }
  if (representations.includes("fraction")) keys.push({ insert: "/", label: "/", aria: "Fraction bar" });
  if (representations.includes("percentage")) keys.push({ insert: "%", label: "%", aria: "Percent" });
  return keys;
};

const OPERATOR_KEYS = {
  "+": { insert: "+", label: "+", aria: "Plus" },
  "-": { insert: "−", label: "−", aria: "Minus" },
  "*": { insert: "×", label: "×", aria: "Times" },
  "/": { insert: "÷", label: "÷", aria: "Divided by" },
  "^": { insert: "^", label: "xⁿ", aria: "To the power of" },
  sqrt: { insert: "√(", label: "√", aria: "Square root" },
  "(": { insert: "(", label: "(", aria: "Open bracket" },
  ")": { insert: ")", label: ")", aria: "Close bracket" },
};

// The declared symbols and operators, a decimal point, and "=" only for an
// equation: declared in the operators and allowed (contract §4.3).
export const expressionKeysFor = ({ symbols, operators, allowEquation }) => [
  ...symbols.map((s) => ({ insert: s, label: s, aria: s, symbol: true })),
  { insert: ".", label: ".", aria: "Decimal point" },
  ...operators.filter((op) => OPERATOR_KEYS[op]).map((op) => OPERATOR_KEYS[op]),
  ...(allowEquation && operators.includes("=") ? [{ insert: "=", label: "=", aria: "Equals" }] : []),
];

const deleteLast = (text) =>
  // "√(" is one key, so it goes in one press.
  text.endsWith("√(") ? text.slice(0, -2) : text.slice(0, -1);

export const MathKeypad = ({ value, onChange, extraKeys, disabled, label = "Keypad" }) => (
  <div className={ui.mathKeypad} role="group" aria-label={label}>
    <div className={ui.mathDigits}>
      {DIGITS.map((digit) => (
        <Key key={digit} label={digit} onPress={() => onChange(value + digit)} disabled={disabled} />
      ))}
    </div>
    {extraKeys.length > 0 && (
      <div className={ui.mathExtras}>
        {extraKeys.map((key) => (
          <Key
            key={key.aria}
            label={key.label}
            aria={key.aria}
            wide={key.symbol && key.label.length > 1}
            onPress={() => onChange(value + key.insert)}
            disabled={disabled}
          />
        ))}
      </div>
    )}
    <div className={ui.mathEdit}>
      <Key label="⌫" aria="Delete" onPress={() => onChange(deleteLast(value))} disabled={disabled || !value} wide />
      <Key label="Clear" aria="Clear answer" onPress={() => onChange("")} disabled={disabled || !value} wide />
    </div>
  </div>
);

// The answer as typed, large and legible. `inputMode="none"` keeps the
// tablet's own keyboard away while still accepting a hardware keyboard.
export const AnswerField = ({
  value,
  onChange,
  label,
  prefix,
  suffix,
  invalid = false,
  describedBy,
  disabled,
  inputRef,
}) => (
  <div className={`${ui.answerField} ${invalid ? ui.answerInvalid : ""}`}>
    {prefix && <span className={ui.answerUnit}>{prefix}</span>}
    <input
      ref={inputRef}
      className={ui.answerInput}
      inputMode="none"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      aria-label={label}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      value={value}
      placeholder="?"
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    />
    {suffix && <span className={ui.answerUnit}>{suffix}</span>}
  </div>
);
